import { useEffect, useState, type ReactNode } from 'react';
import type { StaffGroupDto, StaffMemberDto } from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { staffGroupOptions } from '@/lib/options';
import { FilterBar, FilterField } from '@/components/filters';
import { EMPTY_STAFF_FILTERS, useTeamStore } from '@/stores/team.store';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { count, date } from '@/lib/format';
import { PlusIcon, UsersIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Sheet, TableScroll, Table, Tabs, Td, Th, Tr } from '@/components/page';
import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  TableSkeleton,
  cx,
} from '@/components/ui';
import { SettingsPage } from '@/pages/settings';

/**
 * The team (docs/03-Backend/Authorization.md).
 *
 * Two halves that only make sense together: the people, and the **groups** that
 * say what a job can do. Granting access person by person is how one new hire
 * ends up with a different set from the colleague beside them; a group is the
 * unit a store actually thinks in — "fulfilment", "support".
 *
 * The permission list itself comes from the API (ADR-015), so a permission
 * added server-side is grantable here without a release of this app. Only the
 * labels are local, and an unlabelled permission renders as its key rather than
 * disappearing.
 *
 * The two halves are the page's two tabs; a person or a group opens for
 * editing in a panel beside the list.
 */
export function TeamPage() {
  const {
    members,
    groups,
    groupsLoading,
    areas,
    loading,
    loadingMore,
    saving,
    error,
    nextCursor,
    total,
    filters,
    load,
    loadMore,
    loadGroups,
    loadPermissions,
  } = useTeamStore();

  const me = useAuthStore((state) => state.user);
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [tab, setTab] = useState<'people' | 'groups'>('people');
  const [editing, setEditing] = useState<StaffMemberDto | 'new' | null>(null);
  const [editingGroup, setEditingGroup] = useState<StaffGroupDto | 'new' | null>(null);
  const [resetting, setResetting] = useState<StaffMemberDto | null>(null);
  const [deleting, setDeleting] = useState<StaffGroupDto | null>(null);
  const [unlocking, setUnlocking] = useState<StaffMemberDto | null>(null);

  useEffect(() => {
    void load();
    void loadGroups();
    void loadPermissions();
  }, [load, loadGroups, loadPermissions, locale]);

  return (
    <SettingsPage
      title="team"
      subtitle="settingsTeamHint"
      fill
      actions={
        <Button onClick={() => (tab === 'people' ? setEditing('new') : setEditingGroup('new'))}>
          <PlusIcon className="size-4" />
          {tab === 'people' ? t('addStaff') : t('addGroup')}
        </Button>
      }
      tabs={
        <Tabs
          label={t('team')}
          value={tab}
          onChange={setTab}
          items={[
            { id: 'people', label: t('people') },
            { id: 'groups', label: t('permissionGroups') },
          ]}
        />
      }
    >
      {tab === 'people' ? (
        <ListCard>
          <ListToolbar>
            <FilterBar
              search={filters.search}
              onSearch={(search) => void load({ search })}
              placeholder={t('searchTeamHint')}
              total={total ?? undefined}
              shown={members.length}
              chips={[
                ...(filters.role
                  ? [
                      {
                        key: 'role',
                        label: t(`role${filters.role}` as MessageKey),
                        onRemove: () => void load({ role: '' }),
                      },
                    ]
                  : []),
                ...(filters.groupId
                  ? [
                      {
                        key: 'group',
                        label: groups.find((g) => g.id === filters.groupId)?.name ?? t('staffGroup'),
                        onRemove: () => void load({ groupId: '' }),
                      },
                    ]
                  : []),
                ...(filters.isActive
                  ? [
                      {
                        key: 'state',
                        label: filters.isActive === 'true' ? t('onlyActive') : t('onlyInactive'),
                        onRemove: () => void load({ isActive: '' }),
                      },
                    ]
                  : []),
              ]}
              onClearAll={() => void load(EMPTY_STAFF_FILTERS)}
            >
              <div className="grid gap-4 md:grid-cols-3">
                <FilterField label={t('filterRole')} htmlFor="t-role">
                  <Select
                    id="t-role"
                    value={filters.role}
                    onChange={(event) =>
                      void load({ role: event.target.value as '' | 'STAFF' | 'OWNER' })
                    }
                  >
                    <option value="">{t('allRoles')}</option>
                    <option value="OWNER">{t('roleOWNER')}</option>
                    <option value="STAFF">{t('roleSTAFF')}</option>
                  </Select>
                </FilterField>

                <FilterField label={t('staffGroup')} htmlFor="t-group">
                  <AsyncSelect
                    id="t-group"
                    value={filters.groupId}
                    emptyLabel={t('allGroups')}
                    fetchPage={staffGroupOptions.page}
                    onChange={(id) => void load({ groupId: id })}
                  />
                </FilterField>

                <FilterField label={t('status')} htmlFor="t-active">
                  <Select
                    id="t-active"
                    value={filters.isActive}
                    onChange={(event) => void load({ isActive: event.target.value })}
                  >
                    <option value="">{t('anyState')}</option>
                    <option value="true">{t('onlyActive')}</option>
                    <option value="false">{t('onlyInactive')}</option>
                  </Select>
                </FilterField>
              </div>
            </FilterBar>
          </ListToolbar>

          {error ? (
            <div className="px-4 pt-4">
              <Alert>{error}</Alert>
            </div>
          ) : null}

          {loading ? (
            <TableSkeleton rows={5} columns={5} />
          ) : members.length === 0 ? (
            <EmptyState title={t('noStaff')} body={t('noStaffHint')} icon={<UsersIcon />} />
          ) : (
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('fullName')}</Th>
                    <Th>{t('role')}</Th>
                    <Th>{t('staffGroup')}</Th>
                    <Th>{t('permissions')}</Th>
                    <Th align="end" />
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <Tr key={member.id} onClick={() => setEditing(member)}>
                      <Td>
                        <span className="block text-ink">{member.name}</span>
                        <span dir="ltr" className="block text-xs text-ink-muted">
                          {member.email}
                        </span>
                      </Td>
                      <Td>
                        <Pill tone={member.role === 'OWNER' ? 'info' : 'neutral'}>
                          {t(`role${member.role}` as MessageKey)}
                        </Pill>
                        {!member.isActive ? (
                          <span className="ms-2 align-middle">
                            <Pill tone="danger">{t('deactivated')}</Pill>
                          </span>
                        ) : null}
                        {member.twoFactorEnabled ? (
                          <span className="ms-2 align-middle">
                            <Pill tone="success">{t('twoFactorBadge')}</Pill>
                          </span>
                        ) : null}
                      </Td>
                      <Td className="text-ink-muted">{member.groupName ?? t('noStaffGroup')}</Td>
                      <Td className="text-ink-muted">
                        {/* An owner bypasses every check, so a count would be a
                            lie in the one direction that matters. */}
                        {member.role === 'OWNER'
                          ? t('ownerBypassNote')
                          : t('effectivePermissionCount', {
                              count: count(member.effectivePermissions.length, locale),
                            })}
                      </Td>
                      <Td align="end">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setResetting(member);
                            }}
                          >
                            {t('setStaffPassword')}
                          </Button>
                          {member.twoFactorEnabled ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(event) => {
                                event.stopPropagation();
                                setUnlocking(member);
                              }}
                            >
                              {t('twoFactorReset')}
                            </Button>
                          ) : null}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setEditing(member);
                            }}
                          >
                            {t('editEntry')}
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}

          <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
        </ListCard>
      ) : (
        <ListCard>
          {error ? (
            <div className="px-4 pt-4">
              <Alert>{error}</Alert>
            </div>
          ) : null}

          {groupsLoading && groups.length === 0 ? (
            <TableSkeleton columns={3} />
          ) : groups.length === 0 ? (
            <EmptyState title={t('noGroups')} body={t('noGroupsHint')} icon={<UsersIcon />} />
          ) : (
            <TableScroll>
              <ul className="divide-y divide-line">
                {groups.map((group) => (
                  <li key={group.id} className="px-5 py-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-ink">{group.name}</span>
                      {group.isSystem ? <Pill tone="info">{t('systemGroup')}</Pill> : null}
                      {!group.isActive ? <Pill tone="danger">{t('deactivated')}</Pill> : null}
                      <span className="text-xs text-ink-muted">
                        {t('members')}: <span data-numeric>{count(group.memberCount, locale)}</span>
                      </span>
                      <span className="text-xs text-ink-muted">
                        {t('permissions')}: <span data-numeric>{count(group.permissions.length, locale)}</span>
                      </span>
                      <span className="ms-auto flex gap-1">
                        <Button variant="secondary" size="sm" onClick={() => setEditingGroup(group)}>
                          {t('editEntry')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(group)}>
                          {t('deleteEntry')}
                        </Button>
                      </span>
                    </div>

                    <p className="mt-2 text-xs text-ink-muted">
                      {group.permissions.length === 0
                        ? '—'
                        : group.permissions.map((permission) => label(t, permission)).join(' · ')}
                    </p>

                    <p className="mt-1 text-xs text-ink-faint">
                      {t('groupCreatedOn', { date: date(group.createdAt, locale) })}
                    </p>
                  </li>
                ))}
              </ul>
            </TableScroll>
          )}
        </ListCard>
      )}

      {editing ? (
        <StaffSheet
          member={editing === 'new' ? null : editing}
          groups={groups}
          areas={areas}
          isSelf={editing !== 'new' && editing.id === me?.id}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {editingGroup ? (
        <GroupSheet
          group={editingGroup === 'new' ? null : editingGroup}
          areas={areas}
          onClose={() => setEditingGroup(null)}
        />
      ) : null}

      {resetting ? <PasswordSheet member={resetting} onClose={() => setResetting(null)} /> : null}

      <ConfirmDialog
        open={unlocking !== null}
        title={t('twoFactorReset')}
        body={t('twoFactorResetBody', { name: unlocking?.name ?? '' })}
        confirmLabel={t('twoFactorReset')}
        cancelLabel={t('cancel')}
        busy={saving}
        onCancel={() => setUnlocking(null)}
        onConfirm={async () => {
          if (!unlocking) return;
          const ok = await useTeamStore.getState().resetTwoFactor(unlocking.id);
          if (ok) setUnlocking(null);
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={t('deleteGroup')}
        body={t('deleteGroupBody')}
        confirmLabel={t('deleteEntry')}
        cancelLabel={t('cancel')}
        busy={saving}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          const ok = await useTeamStore.getState().removeGroup(deleting.id);
          if (ok) setDeleting(null);
        }}
      />
    </SettingsPage>
  );
}

// ─── Panels ──────────────────────────────────────────────────────────────────

function StaffSheet({
  member,
  groups,
  areas,
  isSelf,
  onClose,
}: {
  member: StaffMemberDto | null;
  groups: StaffGroupDto[];
  areas: { area: string; permissions: string[] }[];
  isSelf: boolean;
  onClose: () => void;
}) {
  const { saving, createMember, updateMember } = useTeamStore();
  const t = useT();

  const [name, setName] = useState(member?.name ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'STAFF' | 'OWNER'>(member?.role ?? 'STAFF');
  const [groupId, setGroupId] = useState(member?.groupId ?? '');
  const [permissions, setPermissions] = useState<string[]>(member?.permissions ?? []);
  const [isActive, setIsActive] = useState(member?.isActive ?? true);

  const group = groups.find((entry) => entry.id === groupId);

  const submit = async () => {
    const ok = member
      ? await updateMember(member.id, {
          name,
          role,
          staffGroupId: groupId || null,
          permissions,
          isActive,
        })
      : await createMember({
          name,
          email,
          password,
          role,
          staffGroupId: groupId || undefined,
          permissions,
        });

    if (ok) onClose();
  };

  const canSubmit =
    name.trim().length >= 2 && (member ? true : email.includes('@') && password.length >= 12);

  return (
    <Panel
      title={member ? t('editStaff') : t('addStaff')}
      busy={saving}
      disabled={!canSubmit}
      onClose={onClose}
      onSubmit={submit}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('fullName')} htmlFor="s-name">
          <Input id="s-name" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <Field label={t('email')} htmlFor="s-email">
          {/* An email is an identifier: Latin, left to right, and immutable
              once the account exists — changing it changes who signs in. */}
          <Input
            id="s-email"
            dir="ltr"
            type="email"
            value={email}
            disabled={member !== null}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {member ? null : (
          <Field label={t('staffPassword')} htmlFor="s-password" hint={t('staffPasswordHint')}>
            <Input
              id="s-password"
              dir="ltr"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
        )}

        <Field label={t('role')} htmlFor="s-role">
          <Select
            id="s-role"
            value={role}
            // Changing your own role is refused by the API; disabling it here
            // means the refusal is never met by surprise.
            disabled={isSelf}
            onChange={(event) => setRole(event.target.value as 'STAFF' | 'OWNER')}
          >
            <option value="STAFF">{t('roleSTAFF')}</option>
            <option value="OWNER">{t('roleOWNER')}</option>
          </Select>
        </Field>

        <Field label={t('staffGroup')} htmlFor="s-group">
          <AsyncSelect
            id="s-group"
            value={groupId}
            emptyLabel={t('noStaffGroup')}
            fetchPage={staffGroupOptions.page}
            onChange={(id) => setGroupId(id)}
          />
        </Field>

        {member ? (
          <Field label={t('status')} htmlFor="s-active">
            <Select
              id="s-active"
              value={isActive ? 'true' : 'false'}
              disabled={isSelf}
              onChange={(event) => setIsActive(event.target.value === 'true')}
            >
              <option value="true">{t('onlyActive')}</option>
              <option value="false">{t('deactivated')}</option>
            </Select>
          </Field>
        ) : null}
      </div>

      {role === 'OWNER' ? (
        <p className="mt-4 text-xs text-ink-muted">{t('ownerBypassNote')}</p>
      ) : (
        <div className="mt-4">
          <PermissionMatrix
            areas={areas}
            selected={permissions}
            onChange={setPermissions}
            title={t('extraPermissions')}
            hint={t('extraPermissionsHint')}
            inherited={group?.isActive ? group.permissions : []}
          />
        </div>
      )}
    </Panel>
  );
}

function GroupSheet({
  group,
  areas,
  onClose,
}: {
  group: StaffGroupDto | null;
  areas: { area: string; permissions: string[] }[];
  onClose: () => void;
}) {
  const { saving, createGroup, updateGroup } = useTeamStore();
  const t = useT();

  const [nameEn, setNameEn] = useState(group?.translations?.en?.name ?? group?.key ?? '');
  const [nameAr, setNameAr] = useState(group?.translations?.ar?.name ?? '');
  const [permissions, setPermissions] = useState<string[]>(group?.permissions ?? []);
  const [isActive, setIsActive] = useState(group?.isActive ?? true);

  const submit = async () => {
    const translations = { en: { name: nameEn.trim() }, ar: { name: nameAr.trim() || nameEn.trim() } };

    const ok = group
      ? await updateGroup(group.id, { name: nameEn.trim(), translations, permissions, isActive })
      : await createGroup({ name: nameEn.trim(), translations, permissions });

    if (ok) onClose();
  };

  return (
    <Panel
      title={group ? t('editGroup') : t('addGroup')}
      busy={saving}
      disabled={nameEn.trim().length < 2}
      onClose={onClose}
      onSubmit={submit}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('groupNameEn')} htmlFor="g-en">
          <Input id="g-en" dir="ltr" value={nameEn} onChange={(event) => setNameEn(event.target.value)} />
        </Field>
        <Field label={t('groupNameAr')} htmlFor="g-ar">
          <Input id="g-ar" dir="rtl" value={nameAr} onChange={(event) => setNameAr(event.target.value)} />
        </Field>

        {group ? (
          <Field label={t('status')} htmlFor="g-active">
            <Select
              id="g-active"
              value={isActive ? 'true' : 'false'}
              onChange={(event) => setIsActive(event.target.value === 'true')}
            >
              <option value="true">{t('onlyActive')}</option>
              <option value="false">{t('deactivated')}</option>
            </Select>
          </Field>
        ) : null}
      </div>

      <div className="mt-4">
        <PermissionMatrix
          areas={areas}
          selected={permissions}
          onChange={setPermissions}
          title={t('permissions')}
          inherited={[]}
        />
      </div>
    </Panel>
  );
}

function PasswordSheet({ member, onClose }: { member: StaffMemberDto; onClose: () => void }) {
  const { saving, setPassword } = useTeamStore();
  const t = useT();
  const [value, setValue] = useState('');

  return (
    <Panel
      size="md"
      title={t('setStaffPassword')}
      busy={saving}
      disabled={value.length < 12}
      onClose={onClose}
      onSubmit={async () => {
        const ok = await setPassword(member.id, value);
        if (ok) onClose();
      }}
    >
      <p className="text-sm text-ink-muted">{t('setStaffPasswordBody')}</p>
      <p className="mt-2 text-sm text-ink">{member.name}</p>
      <p dir="ltr" className="text-xs text-ink-muted">
        {member.email}
      </p>

      <div className="mt-4">
        <Field label={t('staffPassword')} htmlFor="p-value" hint={t('staffPasswordHint')}>
          <Input
            id="p-value"
            dir="ltr"
            type="password"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </Field>
      </div>
    </Panel>
  );
}

/**
 * The permission picker.
 *
 * Grouped by area as the API describes it, and a permission the group already
 * grants is shown ticked and disabled: a person cannot "un-inherit" one, and a
 * box that looks changeable but is not is worse than one that explains itself.
 */
function PermissionMatrix({
  areas,
  selected,
  onChange,
  title,
  hint,
  inherited,
}: {
  areas: { area: string; permissions: string[] }[];
  selected: string[];
  onChange: (next: string[]) => void;
  title: string;
  hint?: string;
  inherited: string[];
}) {
  const t = useT();

  const toggle = (permission: string) =>
    onChange(
      selected.includes(permission)
        ? selected.filter((entry) => entry !== permission)
        : [...selected, permission],
    );

  return (
    <fieldset className="rounded-card border border-line p-4">
      <legend className="px-1 text-sm font-medium text-ink">{title}</legend>
      {hint ? <p className="mb-3 text-xs text-ink-muted">{hint}</p> : null}

      <div className="grid gap-4 sm:grid-cols-2">
        {areas.map((area) => (
          <div key={area.area}>
            <p className="mb-1.5 text-xs font-medium text-ink-faint">
              {t(`permArea${capitalize(area.area)}` as MessageKey)}
            </p>
            <div className="flex flex-col gap-1">
              {area.permissions.map((permission) => {
                const fromGroup = inherited.includes(permission);

                return (
                  <label
                    key={permission}
                    className={cx(
                      'flex items-center gap-2 text-sm',
                      fromGroup ? 'text-ink-muted' : 'text-ink',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={fromGroup || selected.includes(permission)}
                      disabled={fromGroup}
                      onChange={() => toggle(permission)}
                      className="size-4 rounded border-line accent-brand-600"
                    />
                    {label(t, permission)}
                    {fromGroup ? (
                      <span className="text-[10px] text-ink-faint">({t('fromGroup')})</span>
                    ) : null}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * A side panel with the team's two buttons at its foot: cancel, and save.
 *
 * The store's error is shown in the panel as well as on the page, because the
 * panel covers the page and a refusal nobody can see reads as a save that
 * did nothing.
 */
function Panel({
  title,
  subtitle,
  size = 'lg',
  busy,
  disabled,
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  subtitle?: string;
  size?: 'md' | 'lg';
  busy: boolean;
  disabled: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  children: ReactNode;
}) {
  const t = useT();
  const error = useTeamStore((state) => state.error);
  // The error is shared with the page; only one this panel caused belongs in it.
  const [tried, setTried] = useState(false);

  return (
    <Sheet
      open
      size={size}
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </Button>
          <Button
            disabled={busy || disabled}
            onClick={() => {
              setTried(true);
              void onSubmit();
            }}
          >
            {busy ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      {tried && error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}
      {children}
    </Sheet>
  );
}

/**
 * `products:read` → its label, or the raw key when the API knows a permission
 * this build does not. Rendering the key beats rendering nothing: an
 * unrecognised permission is still one somebody has to be able to grant.
 */
function label(t: ReturnType<typeof useT>, permission: string): string {
  const [area, action] = permission.split(':');
  const key = `perm${capitalize(area ?? '')}${capitalize(action ?? '')}` as MessageKey;
  const translated = t(key);

  return translated === key ? permission : translated;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
